import LatLng from "../model/map/LatLng";
import DictionaryCache from "../helper/DictionaryCache";
import {api} from "../api/api";
import {Option} from "ts-option";

/**
 * Cache for geoCodingAddresses mapped to latLng values - no need to fetch them again.
 */
const cache: DictionaryCache<Option<LatLng>> = new DictionaryCache<Option<LatLng>>(1000);

/**
 * Duration in seconds after which request cached is not valid and can be tried again.
 */
const secondsToInvalidateRequestInCache = 5;

/**
 * Pending requests for google geocoding api.
 * Key represents given geocoding address and promise waits to resolve.
 */
const pendingPromises: { [key: string]: Promise<Option<LatLng>> } = {};

/**
 * Cache for geocoding addresses.
 */
export class GeocodingCache {
	/**
	 * Get coordinates from cache if there is present. If not present, then ask google and store response in cache.
	 * Pending requests are ignored and first pending request for given geocoding address waits to resolve.
	 * @param {string} geocodingAddress geocoding address to convert to LatLng.
	 */
	static getCoords(geocodingAddress: string): Promise<Option<LatLng>> {
		return cache.get(geocodingAddress)
			.map((latLng: Option<LatLng>) => {
				if (pendingPromises[geocodingAddress]) {
					delete pendingPromises[geocodingAddress];
				}
				return Promise.resolve(latLng);
			})
			.getOrElse(() => {
				if (!pendingPromises[geocodingAddress]) {
					pendingPromises[geocodingAddress] = GeocodingCache.askGoogle(geocodingAddress);
				}
				return pendingPromises[geocodingAddress];
			});
	}

	/**
	 * Put coordinates to cache and map to geocoding address.
	 * @param {string} geocodingAddress geocoding address.
	 * @param {Option<LatLng>} latLng lat lng coords.
	 */
	static putCoords(geocodingAddress: string, latLng: Option<LatLng>) {
		cache.store(geocodingAddress, latLng);
	}

	/**
	 * Ask google for lat lng from geocoding address.
	 * @param {string} geocodingAddress geocoding address.
	 * @return {any} LatLng coords or none.
	 */
	private static askGoogle(geocodingAddress: string) {
		return api.instance.googleApi.getLocationFromAddress(geocodingAddress).then((result: Option<LatLng>) => {
			GeocodingCache.putCoords(geocodingAddress, result);
			return GeocodingCache.getCoords(geocodingAddress);
		});
	}
}